import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaUser } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { brandLogo } from '../config/brandAssets';
import { PROGRAM_TITLES } from '../config/programSubNav';
import ProgramMegaMenuLinks from './ProgramMegaMenuLinks';
import { localePath, switchLocalePath, usePublicLocale, usePublicPaths } from '../routes/publicPaths';
import { DEFAULT_NAV_ITEMS, mapNavItemsFromApi, readNavItemsCache, writeNavItemsCache } from '../config/defaultNav';
import { tNavItemName } from '../utils/publicI18n';
import { seedProgramHeroCache, getProgramHeroUrl, getProgramHeroAssets } from '../config/programHeroImages';
import { preloadPublicNavItems, preloadPublicProgrammes } from '../services/siteContentService';
import './Navbar.css';

import { HEADER_MENUS_CLOSE_EVENT } from '../utils/headerMenus';
import { scrollToPageTop } from '../utils/programScroll';
import { prefetchProgramHero, prefetchProgramProjets } from '../utils/prefetchProgramHero';

const navItemKey = (item) => item.id ?? item.name;

const getProfilePath = (user) => {
  if (!user) return '/login';
  switch (user.role) {
    case 'admin':
      return '/admin';
    case 'operateur':
      return '/operateur';
    case 'maitresse':
      return '/maitresse';
    case 'donateur':
      return '/donateur';
    default:
      return '/login';
  }
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const locale = usePublicLocale();
  const paths = usePublicPaths();
  const [navItems, setNavItems] = useState(() => readNavItemsCache() || DEFAULT_NAV_ITEMS);
  const [programmesBySlug, setProgrammesBySlug] = useState(() => {
    try {
      const raw = localStorage.getItem('khayr_public_programmes_v2');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [megaOpenSlug, setMegaOpenSlug] = useState(null);
  const headerWrapRef = useRef(null);
  const location = useLocation();
  const profilePath = getProfilePath(user);
  const accountLabel = user ? t('common.profile') : t('common.login');

  useEffect(() => {
    preloadPublicNavItems()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const mapped = mapNavItemsFromApi(data);
          setNavItems(mapped);
          writeNavItemsCache(mapped);
        }
      })
      .catch(() => {});

    preloadPublicProgrammes()
      .then((data) => {
        if (Array.isArray(data)) {
          const bySlug = Object.fromEntries(data.map((p) => [p.slug, p]));
          setProgrammesBySlug(bySlug);
          try {
            localStorage.setItem('khayr_public_programmes_v2', JSON.stringify(bySlug));
          } catch {
            // ignore quota errors
          }
          seedProgramHeroCache(data);
        }
      })
      .catch(() => {});
  }, []);

  const dropdownCloseTimer = useRef(null);

  const cancelDropdownClose = useCallback(() => {
    if (dropdownCloseTimer.current) {
      clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
  }, []);

  const closeMenus = useCallback(() => {
    cancelDropdownClose();
    setMegaOpenSlug(null);
    setOpenDropdown(null);
  }, [cancelDropdownClose]);

  const isOnProgramDetailPage = /\/programmes\/[^/]+/.test(location.pathname);

  useEffect(() => {
    setIsOpen(false);
    closeMenus();
  }, [location.pathname, location.hash, closeMenus]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (!headerWrapRef.current?.contains(event.target)) {
        closeMenus();
      }
    };

    const handleCloseEvent = () => closeMenus();
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const nextY = window.scrollY;
      const delta = Math.abs(nextY - lastScrollY);
      lastScrollY = nextY;
      if (delta < 12) return;
      closeMenus();
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') closeMenus();
    };

    document.addEventListener('pointerdown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, { passive: true, capture: true });
    document.addEventListener('keydown', handleEscape);
    window.addEventListener(HEADER_MENUS_CLOSE_EVENT, handleCloseEvent);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener(HEADER_MENUS_CLOSE_EVENT, handleCloseEvent);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [closeMenus]);

  useEffect(() => () => cancelDropdownClose(), [cancelDropdownClose]);

  const switchLanguage = (lng) => {
    const next = switchLocalePath(`${location.pathname}${location.search}${location.hash}`, lng);
    i18n.changeLanguage(lng);
    if (next !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(next);
    }
  };

  const itemPath = (path) => localePath(path, locale);

  const isActive = (path) => location.pathname === itemPath(path);

  const isProgramActive = (slug) =>
    location.pathname === paths.programme(slug) ||
    location.pathname.startsWith(`${paths.programme(slug)}/`);

  const isMegaMenuVisible = (slug) =>
    megaOpenSlug === slug && !isOnProgramDetailPage && !isProgramActive(slug);

  const activeMegaSlug = isOnProgramDetailPage ? null : megaOpenSlug;

  const handleProgramNavPointerDown = () => {
    closeMenus();
  };

  const handleProgramNavClick = (slug, event) => {
    closeMenus();
    const programPath = paths.programme(slug);
    if (location.pathname === programPath) {
      event.preventDefault();
      window.history.replaceState(null, '', programPath);
      scrollToPageTop('auto');
    }
  };

  const toggleMegaMenu = (slug, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isOnProgramDetailPage || isProgramActive(slug)) {
      closeMenus();
      return;
    }
    setMegaOpenSlug((prev) => (prev === slug ? null : slug));
    setOpenDropdown(null);
  };

  const handleDropdownEnter = () => {
    cancelDropdownClose();
  };

  const handleDropdownLeave = (event) => {
    const related = event.relatedTarget;
    if (related instanceof Node && event.currentTarget.contains(related)) {
      return;
    }
    cancelDropdownClose();
    dropdownCloseTimer.current = setTimeout(() => {
      setOpenDropdown(null);
      dropdownCloseTimer.current = null;
    }, 220);
  };

  const handleMenuAreaLeave = (event) => {
    const related = event.relatedTarget;
    if (related instanceof Node && headerWrapRef.current?.contains(related)) {
      return;
    }
    cancelDropdownClose();
    dropdownCloseTimer.current = setTimeout(() => {
      closeMenus();
    }, 220);
  };

  const handleHeaderMouseLeave = (event) => {
    const related = event.relatedTarget;
    if (related instanceof Node && event.currentTarget.contains(related)) {
      return;
    }
    cancelDropdownClose();
    dropdownCloseTimer.current = setTimeout(() => {
      closeMenus();
    }, 220);
  };

  const handleMegaItemEnter = (slug) => {
    void prefetchProgramHero(slug, programmesBySlug[slug]);
    prefetchProgramProjets(slug);
  };

  const programLinkState = (slug) => {
    const program = programmesBySlug[slug];
    const assets = getProgramHeroAssets(slug);
    const heroImage = assets.image_url || program?.image_url || getProgramHeroUrl(slug) || null;
    if (!heroImage && !assets.image_url_webp) return undefined;
    return {
      heroImage,
      heroWebp: assets.image_url_webp || program?.image_url_webp || null,
      heroWebpSm: assets.image_url_webp_sm || program?.image_url_webp_sm || null,
    };
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { opacity: 1, height: 'auto', transition: { duration: 0.25 } },
    exit: { opacity: 0, height: 0, transition: { duration: 0.2 } },
  };

  return (
    <>
      <header
        className="site-header fixed top-0 left-0 right-0 z-50"
        onMouseEnter={cancelDropdownClose}
        onMouseLeave={handleHeaderMouseLeave}
      >
        <div className="site-topbar">
          <div className="site-header__container site-topbar__inner">
            <Link to={profilePath} className="site-topbar__login">
              <FaUser className="site-topbar__login-icon" aria-hidden="true" />
              <span>{accountLabel}</span>
            </Link>
          </div>
        </div>

        <div
          ref={headerWrapRef}
          className="site-mainbar-wrap"
          onMouseEnter={cancelDropdownClose}
          onMouseLeave={handleMenuAreaLeave}
        >
          <div className="site-mainbar">
            <div className="site-header__container">
              <div className="site-header__inner">
                <Link to={paths.home} className="site-logo" aria-label={t('public.brand.name')}>
                  <div className="site-logo__box">
                    <img src={brandLogo} alt="" className="site-logo__icon" />
                    <span className="site-logo__title">{t('public.brand.name')}</span>
                    <span className="site-logo__subtitle">SOHBAT KHEIR</span>
                  </div>
                </Link>

                <nav className="site-nav" aria-label={t('public.nav.mainMenu')}>
                  {navItems.map((item) =>
                    item.path ? (
                      <Link
                        key={navItemKey(item)}
                        to={itemPath(item.path)}
                        className={`site-nav__link ${isActive(item.path) ? 'site-nav__link--active' : ''}`}
                        onClick={closeMenus}
                      >
                        {tNavItemName(t, item)}
                      </Link>
                    ) : item.megaMenu ? (
                      <div
                        key={navItemKey(item)}
                        className={`site-nav__item site-nav__item--mega ${
                          isMegaMenuVisible(item.slug) ? 'site-nav__item--mega-open' : ''
                        }`}
                        onMouseEnter={() => handleMegaItemEnter(item.slug)}
                      >
                        <div className="site-nav__mega-trigger">
                          <Link
                            to={paths.programme(item.slug)}
                            state={programLinkState(item.slug)}
                            className={`site-nav__link site-nav__mega-label ${
                              isProgramActive(item.slug) ? 'site-nav__link--program' : ''
                            }`}
                            onPointerDown={() => {
                              handleProgramNavPointerDown();
                              prefetchProgramHero(item.slug, programmesBySlug[item.slug]);
                            }}
                            onClick={(e) => handleProgramNavClick(item.slug, e)}
                          >
                            {tNavItemName(t, item)}
                          </Link>
                          {!isOnProgramDetailPage && !isProgramActive(item.slug) && (
                          <button
                            type="button"
                            className="site-nav__trigger site-nav__mega-toggle"
                            aria-expanded={isMegaMenuVisible(item.slug)}
                            aria-label={`${tNavItemName(t, item)} — menu`}
                            onClick={(e) => toggleMegaMenu(item.slug, e)}
                          >
                            <FaChevronDown className="site-nav__chevron" />
                          </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        key={navItemKey(item)}
                        className={`site-nav__item ${openDropdown === item.name ? 'site-nav__item--open' : ''}`}
                        onMouseEnter={handleDropdownEnter}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <button
                          type="button"
                          className="site-nav__trigger"
                          aria-expanded={openDropdown === item.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMegaOpenSlug(null);
                            setOpenDropdown((prev) => (prev === item.name ? null : item.name));
                          }}
                        >
                          {tNavItemName(t, item)}
                          <FaChevronDown className="site-nav__chevron" />
                        </button>
                        <div className="site-nav__dropdown">
                          {item.children.map((child) => (
                            <Link
                              key={child.path + child.name}
                              to={itemPath(child.path)}
                              className="site-nav__dropdown-link"
                              onClick={closeMenus}
                            >
                              {tNavItemName(t, child)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </nav>

                <div className="site-header__actions">
                  <Link to={profilePath} className="site-header__profile">
                    <FaUser className="site-header__profile-icon" aria-hidden="true" />
                    <span>{accountLabel}</span>
                  </Link>
                  <div className="site-header__lang">
                    <button type="button" onClick={() => switchLanguage('fr')} className={locale === 'fr' ? 'is-active' : ''}>FR</button>
                    <button type="button" onClick={() => switchLanguage('ar')} className={locale === 'ar' ? 'is-active' : ''}>عربي</button>
                  </div>
                </div>

                <button type="button" className="site-mobile-toggle" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
                  {isOpen ? <FaTimes className="h-6 w-6" /> : <FaBars className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeMegaSlug && (
              <motion.div
                key={activeMegaSlug}
                className="site-mega-menu"
                onMouseLeave={handleMenuAreaLeave}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.12 }}
              >
                <div className="site-header__container">
                  <ProgramMegaMenuLinks
                    slug={activeMegaSlug}
                    title={PROGRAM_TITLES[activeMegaSlug]}
                    variant="mega"
                    program={programmesBySlug[activeMegaSlug]}
                    onNavigate={closeMenus}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div className="site-mobile-menu lg:hidden" initial="hidden" animate="visible" exit="exit" variants={mobileMenuVariants}>
              <div className="site-header__container">
                <Link to={profilePath} className="site-mobile-menu__link">
                  {accountLabel}
                </Link>
                {navItems.map((item) =>
                  item.path ? (
                    <Link key={navItemKey(item)} to={itemPath(item.path)} className="site-mobile-menu__link">{tNavItemName(t, item)}</Link>
                  ) : item.megaMenu ? (
                    <div key={navItemKey(item)}>
                      <div className="site-mobile-menu__section-title">{tNavItemName(t, item)}</div>
                      <ProgramMegaMenuLinks
                        slug={item.slug}
                        title={PROGRAM_TITLES[item.slug]}
                        variant="mega"
                        program={programmesBySlug[item.slug]}
                        onNavigate={closeMenus}
                      />
                    </div>
                  ) : (
                    <div key={navItemKey(item)}>
                      <div className="site-mobile-menu__section-title">{tNavItemName(t, item)}</div>
                      {item.children.map((child) => (
                        <Link key={child.path + child.name} to={itemPath(child.path)} className="site-mobile-menu__sublink">{tNavItemName(t, child)}</Link>
                      ))}
                    </div>
                  )
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <div className="site-header-spacer" aria-hidden="true" />
    </>
  );
};

export default Navbar;
